import React from 'react';
import useSEO from "./useSEO";

const Healthcare = () => {
  useSEO({
    title: "Healthcare Services in Hyderabad | Hospital & Medical Infrastructure Solutions",
    description:
      "Comprehensive healthcare solutions in Hyderabad offering hospital management, medical equipment setup, and digital healthcare services. We help hospitals and clinics enhance efficiency, patient experience, and compliance.",
    keywords:
      "healthcare services Hyderabad, hospital management Hyderabad, healthcare consulting Hyderabad, medical infrastructure setup, clinic setup Hyderabad, digital healthcare solutions, hospital design Hyderabad, healthcare IT systems, healthcare project management, medical equipment installation Hyderabad",
    ogTitle: "Healthcare Services in Hyderabad - Hospital & Medical Solutions",
    ogDescription:
      "Professional healthcare solutions for hospitals and clinics in Hyderabad, covering setup, operations, technology, and compliance.",
    ogImage: "imgs/healthcare-process.jpg",
    twitterTitle: "Healthcare Services in Hyderabad - Hospital & Medical Solutions",
    twitterDescription:
      "Expert healthcare services for hospitals, clinics, and medical centers in Hyderabad. From design to digital transformation — we deliver excellence.",
    twitterImage: "imgs/healthcare-process.jpg",
  });

  return (
    <div>
      <>
        {/* Section 1 - Introduction */}
        <section className="pt-lg-8 pt-xl-9">
          <div className="container position-relative pt-4 pt-lg-0">
            <div className="row align-items-center">
              {/* Left Text */}
              <div className="col-md-7 col-xl-6 mb-7 mb-md-0">
                <h1 className="mb-4">Healthcare Services</h1>
                <p className="mb-4">
                  <b>Healthcare</b> is the foundation of human well-being, and we
                  specialize in delivering innovative, reliable, and sustainable
                  healthcare solutions. From hospital infrastructure design to
                  digital health systems, our goal is to help medical institutions
                  improve care quality, efficiency, and patient satisfaction.
                </p>
                <p className="mb-4">
                  Our team of experts supports hospitals, clinics, and diagnostic
                  centers in planning, implementing, and optimizing healthcare
                  operations. We combine medical expertise with technology-driven
                  strategies to build modern, compliant, and patient-centric
                  healthcare environments.
                </p>
              </div>

              {/* Right Image */}
              <div className="col-md-5 ms-auto text-center">
                <img
                  src="imgs/healthcare.jpg"
                  alt="Healthcare Services"
                  className="rounded-3 shadow-sm w-100"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 - Key Services */}
        <section className="overflow-hidden pb-0">
          <div className="container">
            <div className="row align-items-center">
              {/* Image */}
              <div className="col-lg-6 text-center position-relative mb-5 mb-lg-0">
                <div className="pe-lg-6 pe-xl-8">
                  <img
                    src="imgs/healthcare-process.jpg"
                    alt="Healthcare Infrastructure"
                    className="rounded-4 shadow-sm w-100"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="col-lg-6 ms-auto">
                <h2 className="mb-lg-4">Our Healthcare Solutions Include:</h2>
                <hr className="my-4 my-lg-5" />

                <ul className="list-group list-group-borderless">
                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Hospital Planning & Infrastructure Setup</b>
                  </li>
                  <p>
                    Designing and developing hospitals, clinics, and diagnostic
                    centers with optimized layouts and patient flow management.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Medical Equipment & Facility Installation</b>
                  </li>
                  <p>
                    End-to-end sourcing, installation, and calibration of medical
                    devices and healthcare infrastructure equipment.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Healthcare IT & Digital Systems</b>
                  </li>
                  <p>
                    Implementation of hospital management systems (HMS), EHRs,
                    and telemedicine platforms for seamless digital healthcare
                    operations.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Regulatory & Compliance Consulting</b>
                  </li>
                  <p>
                    Helping healthcare institutions comply with NABH, NABL, WHO,
                    and government healthcare standards.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Facility Operations & Maintenance</b>
                  </li>
                  <p>
                    Managing daily hospital operations, preventive maintenance,
                    and staff coordination for smooth functioning.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Healthcare Design & Interiors</b>
                  </li>
                  <p>
                    Designing functional and aesthetic healthcare spaces that
                    promote healing, hygiene, and efficiency.
                  </p>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3 - Benefits */}
        <section className="py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="text-primary">Why Choose Our Healthcare Services</h2>
              <p className="lead">
                We bridge healthcare expertise with innovation to deliver
                reliable, modern, and patient-centric solutions.
              </p>
            </div>

            <div className="row text-center">
              <div className="col-md-4 mb-4">
                <i className="bi bi-hospital text-success fs-3 mb-2"></i>
                <h5>End-to-End Hospital Solutions</h5>
                <p>
                  From feasibility studies to commissioning — we handle every
                  stage of hospital setup and expansion.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-shield-check text-success fs-3 mb-2"></i>
                <h5>Regulatory Compliance</h5>
                <p>
                  Our services strictly follow NABH, NABL, and WHO guidelines to
                  ensure safety, hygiene, and accreditation.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-heart-pulse text-success fs-3 mb-2"></i>
                <h5>Patient-Centric Approach</h5>
                <p>
                  Every solution is designed to improve the patient journey —
                  from admission to discharge.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-gear-wide-connected text-success fs-3 mb-2"></i>
                <h5>Technology-Driven Systems</h5>
                <p>
                  Integrating modern IT solutions like telemedicine, IoT
                  monitoring, and hospital automation systems.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-cash-stack text-success fs-3 mb-2"></i>
                <h5>Cost-Effective Implementation</h5>
                <p>
                  Optimized project planning to deliver high-quality healthcare
                  infrastructure within budget.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-lightbulb text-success fs-3 mb-2"></i>
                <h5>Innovative Design Concepts</h5>
                <p>
                  Blending aesthetics and efficiency to create healing
                  environments that inspire confidence and comfort.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    </div>
  );
};

export default Healthcare;
